import React from "react";

import Link from "next/link";

import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { PDP_MEN_PAGES, PDP_PAGES } from "@/constants";
import rawContentfulLayoutData from "@/data/contentfulLayoutData.json";
import { TargetAttribute } from "@/types/common";
import { LayoutData } from "@/types/layoutData";

/**
 * @interface LinkHandlerProps
 * @description Defines the properties for a component that handles navigation links.
 * @property {string} path - The target URL path for the link.
 * @property {(e: React.MouseEvent<HTMLAnchorElement>) => void} [onClick] - Optional click event handler for the link.
 * @property {React.ReactNode} children - The content (e.g., text, other components) to be rendered inside the link.
 * @property {string} [className] - Optional CSS class names to apply to the link.
 * @property {string} [ariaLabel] - Optional ARIA label for accessibility.
 * @property {boolean} [ariaExpanded] - Optional ARIA expanded state for accessibility.
 * @property {string} [id] - Optional HTML ID attribute for the link.
 */
export interface LinkHandlerProps {
  path: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: React.ReactNode;
  className?: string;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaDisabled?: boolean;
  id?: string;
}

/*
 * --- Module Scope ---
 * This runs once on load. It filters and maps the JSON into a Set for O(1) lookup.
 */
const internalPathsMap = new Set(
  (rawContentfulLayoutData as LayoutData[]).map((item) => item.url_slug)
);

const CORE_HGN_PDP_PATHS = new Set([...PDP_PAGES, ...PDP_MEN_PAGES]);

function normalizeInternalPath(path: string) {
  const stringPath = path.split(/[?#]/)[0].replace(/^\/+|\/+$/g, "");
  return stringPath === "" ? "/" : `/${stringPath}/`;
}

export function shouldPrefetchPath(path: string) {
  return CORE_HGN_PDP_PATHS.has(normalizeInternalPath(path));
}

export const LinkHandler = ({
  path,
  onClick,
  children,
  className = "",
  ariaLabel,
  ariaExpanded,
  ariaDisabled,
  id,
}: LinkHandlerProps) => {
  if (typeof path !== "string") {
    // eslint-disable-next-line no-console
    console.error(
      "ERROR: LinkHandler path issue",
      JSON.stringify(
        {
          path,
          onClick,
          className,
          ariaLabel,
          ariaExpanded,
          id,
        },
        null,
        2
      )
    );
    // eslint-disable-next-line no-console
    console.error("Children added to TextElementNova instead:", children);
    return <TextElementNova className={className}>{children}</TextElementNova>;
  }

  // Separate path and search, Normalize: "test" -> "/test/"
  const stringPath = normalizeInternalPath(path);

  if (path !== stringPath) {
    //console.log("stringPath changes ", path, stringPath);
  }

  // Determine if the path is internal
  const isInternal = internalPathsMap.has(stringPath);

  let externalPath = path;
  // Default target for external links
  let target: TargetAttribute = "_top";

  const hasProtocol = (url: string) => /^(https?|tel|mailto):/i.test(url);
  const isTelOrMailto = /^(tel|mailto):/i.test(path);
  const isBlog = path.includes("/blog/");

  // If the link is NOT isNextJs, construct the appropriate external path and target
  if (!isInternal) {
    if (hasProtocol(path)) {
      // tel/mailto must stay same-tab; _blank for other external domains
      if (
        !isTelOrMailto &&
        !path.includes("nutrafol.com") &&
        !path.includes("vercel.app") &&
        !path.includes("localhost")
      ) {
        target = "_blank";
      }
    } else {
      // if has NO Protocol, prepend the base URL for external paths
      externalPath = process.env.NEXT_PUBLIC_BASEURL + path;
    }
  }

  if (isBlog) {
    target = "_blank";
  }

  //console.log("path", path, "isInternal", isInternal, target);

  return (
    <>
      {isInternal ? (
        // Render Next.js Link for internal paths
        <Link
          href={path}
          onClick={onClick}
          className={`link internal ${className}`}
          aria-label={ariaLabel}
          aria-expanded={ariaExpanded}
          aria-disabled={ariaDisabled || undefined}
          id={id}
          prefetch={shouldPrefetchPath(stringPath)}
        >
          {children}
        </Link>
      ) : (
        // Render a standard <a> tag for external paths
        <a
          href={externalPath}
          onClick={onClick}
          target={target}
          className={`link external ${className}`}
          id={id}
          // Add rel="noopener noreferrer" for security when using target="_blank"
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
          aria-label={ariaLabel}
          aria-expanded={ariaExpanded}
          aria-disabled={ariaDisabled || undefined}
        >
          {children}
        </a>
      )}
    </>
  );
};
